import React from 'react';
import { withRouter } from 'react-router-dom';
import set from 'lodash/set';
import get from 'lodash/get';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Popper,
} from '@mui/material';

import StatusPill from '../common/status-pill';
import FieldReportEditForm from './fieldReport-edit-form';
import { FieldReportStatuses } from '../../lib/utils';

const styles = {
  dialogPaper: {
    minHeight: '95vh',
    maxHeight: '95vh',
  },
};

class FieldReportEditModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fieldReport: props.fieldReport,
      error: false,
      errorMessage: '',
      buttonsOpened: false,
    };

    this.contentRef = React.createRef(null);
    this.actionButtonsRef = React.createRef(null);

    this.toggleButtons = this.toggleButtons.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleLA311ResolutionCode = this.handleLA311ResolutionCode.bind(this);
    this.handleAddTask = this.handleAddTask.bind(this);
    this.handleAddTicket = this.handleAddTicket.bind(this);
    this.handleCloseCase = this.handleCloseCase.bind(this);
    this.setError = this.setError.bind(this);
  }

  toggleButtons() {
    const { buttonsOpened } = this.state;
    this.setState({ buttonsOpened: !buttonsOpened });
  }

  handleChange(event, instantSave) {
    const { fieldReport } = this.state;
    const { name, value } = event.target;

    if (fieldReport[name] === value) return;

    const newFieldReport = {
      ...fieldReport,
      [name]: value,
    };
    this.setState(
      { fieldReport: newFieldReport, error: false },
      () => {
        if (instantSave) this.handleSave();
      },
    );
  }

  handleLA311ResolutionCode(value) {
    const { fieldReport } = this.state;
    const newFieldReport = {
      ...fieldReport,
    };
    set(newFieldReport, 'la311.resolutionCode', value);
    this.setState({ fieldReport: newFieldReport, error: false });
  }

  setError(message) {
    const { fieldReport } = this.state;

    this.contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });

    return this.setState({
      error: true,
      errorMessage: message,
      buttonsOpened: false,
      fieldReport: {
        ...fieldReport,
        status: FieldReportStatuses.OPEN,
      },
    });
  }

  handleSave(fieldReportValue) {
    const fieldReport = fieldReportValue || this.state.fieldReport;
    const { editing, onSave } = this.props;

    const is311Case = get(fieldReport, 'la311.createdBy311', false);
    const resolutionCode = get(fieldReport, 'la311.resolutionCode', '');
    const isClosed = get(fieldReport, 'status', '') === FieldReportStatuses.CLOSED;
    if (isClosed && is311Case) {
      // don't allow close w/o resolution code
      if (!resolutionCode) {
        return this.setError('Cannot close LA 311 report without resolution code.');
      }
    }

    if (!isClosed && resolutionCode) {
      return this.setState({ error: true, errorMessage: 'Can only set resolution code when closing case.' });
    }

    return onSave(fieldReport, editing, true);
  }

  handleCloseCase() {
    const { fieldReport } = this.state;
    const newFieldReport = {
      ...fieldReport,
      status: FieldReportStatuses.CLOSED,
    };

    this.setState(
      { error: false, buttonsOpened: false },
      async () => {
        const res = await this.handleSave(newFieldReport);
        if (res) {
          this.setState({ fieldReport: newFieldReport });
        }
      },
    );
  }

  handleAddTask(event) {
    this.setState({ buttonsOpened: false });
    this.props.onAddTask(event, this.state.fieldReport);
  }

  handleAddTicket(event) {
    this.setState({ buttonsOpened: false });
    this.props.onAddTicket(event, this.state.fieldReport);
  }

  render() {
    const { fieldReport, errorMessage, buttonsOpened } = this.state;
    const {
      isOpen,
      onCancel,
      editing,
      vehicles,
      caseUrls,
      tz,
    } = this.props;

    return (
      <Dialog
        PaperProps={{ style: styles.dialogPaper }}
        open={isOpen}
        fullWidth
        maxWidth="md"
        onClose={onCancel}
      >
        <DialogTitle>
          <Grid sx={{ display: 'inline', mr: 2 }}>
            {`Case - ${fieldReport.category || ''}`}
          </Grid>
          <StatusPill status={fieldReport.status} />
        </DialogTitle>
        <DialogContent ref={this.contentRef}>
          <FieldReportEditForm
            caseUrls={caseUrls}
            handleChange={this.handleChange}
            handleLA311ResolutionCode={this.handleLA311ResolutionCode}
            fieldReport={fieldReport}
            editing={editing}
            vehicles={vehicles}
            errorMessage={errorMessage}
            tz={tz}
          />
        </DialogContent>
        <DialogActions ref={this.actionButtonsRef}>
          <Button onClick={onCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          {fieldReport.status !== FieldReportStatuses.CLOSED && (
            <Button
              onClick={this.toggleButtons}
              color="primary"
              variant="contained"
            >
              <Grid sx={{ pr: 1.5, mr: 1.5, borderRight: '1px solid #ffffff45' }}>Close Case</Grid>
              <Grid style={{ transform: buttonsOpened ? 'rotate(180deg)' : '' }}>
                &#9662;
              </Grid>
            </Button>
          )}
          <Popper
            open={buttonsOpened}
            anchorEl={this.actionButtonsRef.current}
            placement="top-end"
            modifiers={[{
              name: 'flip',
              enabled: false,
            }]}
            style={{ zIndex: 9999 }}
          >
            <Grid
              container
              flexDirection="column"
              sx={{ my: 0.5, px: 2, py: 1, bgcolor: 'white' }}
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            >
              <Button
                name="PickupTack"
                color="primary"
                variant="contained"
                sx={{ mb: 1 }}
                onClick={this.handleAddTask}
              >
                Create Pickup Task and Close Case
              </Button>
              <Button
                name="RepairTicket"
                color="primary"
                variant="contained"
                onClick={this.handleAddTicket}
                sx={{ mb: 1 }}
              >
                Create Repair Ticket, Pickup Task and Close Case
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={this.handleCloseCase}
              >
                Close Case
              </Button>
            </Grid>
          </Popper>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
}, dispatch);

const mapStateToProps = () => ({
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(FieldReportEditModal));
